import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Checkbox,
  CheckboxGroup,
  Stack,
  SimpleGrid,
  useToast,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * ExportBuilder - Multi-format data export interface
 */
function ExportBuilder({ poll, pollId }) {
  const [exportType, setExportType] = useState('microdata'); // microdata or crosstabs
  const [format, setFormat] = useState('csv');
  const [includeWeights, setIncludeWeights] = useState(true);
  const [includeQuality, setIncludeQuality] = useState(true);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [weighted, setWeighted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState([]);
  const toast = useToast();

  React.useEffect(() => {
    if (exportType === 'crosstabs') {
      fetchDimensions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportType, pollId]);

  const fetchDimensions = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/available-demographics/`);
      if (response.ok) {
        const data = await response.json();
        setDimensions(data.dimensions || []);
      }
    } catch (err) {
      console.error('Error fetching dimensions:', err);
    }
  };

  const handleExportMicrodata = async () => {
    try {
      setLoading(true);

      const response = await fetchWithAuth(`/api/polls/${pollId}/export-microdata/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          format,
          include_weights: includeWeights,
          include_quality: includeQuality,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `poll_${pollId}_microdata.${format}`;
      if (contentDisposition) {
        const matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (matches) filename = matches[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Export Successful',
        description: `Downloaded ${filename}`,
        status: 'success',
        duration: 5000,
      });
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: 'Export Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCrosstabs = async () => {
    if (selectedDimensions.length === 0) {
      toast({
        title: 'No dimensions selected',
        description: 'Please select at least one demographic dimension',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);

      const response = await fetchWithAuth(`/api/polls/${pollId}/export-crosstabs/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dimensions: selectedDimensions,
          weighted,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poll_${pollId}_crosstabs.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Export Successful',
        description: 'Downloaded crosstabs Excel file',
        status: 'success',
        duration: 5000,
      });
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: 'Export Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Instructions */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Export Poll Data</Text>
          <Text fontSize="sm" mt={1}>
            Export your poll data for analysis in external tools. Choose microdata
            (respondent-level data) or crosstabs (summary tables).
          </Text>
        </Box>
      </Alert>

      {/* Export Type Selection */}
      <Box
        p={5}
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Export Type</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box
            p={4}
            borderWidth="2px"
            borderColor={exportType === 'microdata' ? 'blue.500' : 'gray.200'}
            borderRadius="md"
            cursor="pointer"
            onClick={() => setExportType('microdata')}
            bg={exportType === 'microdata' ? 'blue.50' : 'white'}
            _hover={{ borderColor: 'blue.300' }}
          >
            <VStack align="start" spacing={2}>
              <HStack>
                <Badge colorScheme="blue">Microdata</Badge>
              </HStack>
              <Text fontWeight="medium">Respondent-Level Data</Text>
              <Text fontSize="sm" color="gray.600">
                Each row = one respondent with all their responses and demographics.
                Best for custom analysis in SPSS, Stata, R, or Excel.
              </Text>
            </VStack>
          </Box>

          <Box
            p={4}
            borderWidth="2px"
            borderColor={exportType === 'crosstabs' ? 'blue.500' : 'gray.200'}
            borderRadius="md"
            cursor="pointer"
            onClick={() => setExportType('crosstabs')}
            bg={exportType === 'crosstabs' ? 'blue.50' : 'white'}
            _hover={{ borderColor: 'blue.300' }}
          >
            <VStack align="start" spacing={2}>
              <HStack>
                <Badge colorScheme="purple">Crosstabs</Badge>
              </HStack>
              <Text fontWeight="medium">Summary Tables</Text>
              <Text fontSize="sm" color="gray.600">
                Pre-calculated crosstabs by demographic dimensions.
                Best for quick reporting and presentations.
              </Text>
            </VStack>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Microdata Export Options */}
      {exportType === 'microdata' && (
        <>
          <Box
            p={5}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Heading size="sm" mb={4}>Format Options</Heading>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Export Format</FormLabel>
                <Select value={format} onChange={(e) => setFormat(e.target.value)}>
                  <option value="csv">CSV - Comma Separated Values</option>
                  <option value="excel">Excel - .xlsx (with formatting)</option>
                  <option value="spss">SPSS - .sav (Statistical Package)</option>
                  <option value="stata">Stata - .dta (Statistical Software)</option>
                </Select>
                <Text fontSize="xs" color="gray.600" mt={2}>
                  {format === 'csv' && 'Universal format, works with any software'}
                  {format === 'excel' && 'Formatted Excel workbook with multiple sheets'}
                  {format === 'spss' && 'For IBM SPSS Statistics (includes variable labels)'}
                  {format === 'stata' && 'For Stata statistical software'}
                </Text>
              </FormControl>

              <Divider />

              <VStack spacing={3} align="start">
                <Text fontSize="sm" fontWeight="medium">Include in Export:</Text>
                <Checkbox
                  isChecked={includeWeights}
                  onChange={(e) => setIncludeWeights(e.target.checked)}
                >
                  <Text fontSize="sm">Demographic weights (if calculated)</Text>
                </Checkbox>
                <Checkbox
                  isChecked={includeQuality}
                  onChange={(e) => setIncludeQuality(e.target.checked)}
                >
                  <Text fontSize="sm">Response quality metrics</Text>
                </Checkbox>
              </VStack>
            </VStack>
          </Box>

          <Button
            colorScheme="blue"
            size="lg"
            leftIcon={<DownloadIcon />}
            onClick={handleExportMicrodata}
            isLoading={loading}
            loadingText="Exporting..."
          >
            Download Microdata ({format.toUpperCase()})
          </Button>
        </>
      )}

      {/* Crosstabs Export Options */}
      {exportType === 'crosstabs' && (
        <>
          <Box
            p={5}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Heading size="sm" mb={4}>Crosstab Options</Heading>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Select Demographic Dimensions</FormLabel>
                {dimensions.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No demographic dimensions available
                  </Text>
                ) : (
                  <CheckboxGroup
                    value={selectedDimensions}
                    onChange={setSelectedDimensions}
                  >
                    <Stack spacing={2}>
                      {dimensions.map(dim => (
                        <Checkbox key={dim} value={dim}>
                          <Text fontSize="sm">{dim}</Text>
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                )}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {selectedDimensions.length} dimension{selectedDimensions.length !== 1 ? 's' : ''} selected
                </Text>
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="weighted-crosstabs" mb={0} fontSize="sm">
                  Apply demographic weights
                </FormLabel>
                <Switch
                  id="weighted-crosstabs"
                  isChecked={weighted}
                  onChange={(e) => setWeighted(e.target.checked)}
                  colorScheme="blue"
                />
              </FormControl>
            </VStack>
          </Box>

          <Alert status="info" fontSize="sm">
            <AlertIcon />
            Crosstabs will be exported as an Excel workbook with one sheet per
            question/dimension combination.
          </Alert>

          <Button
            colorScheme="blue"
            size="lg"
            leftIcon={<DownloadIcon />}
            onClick={handleExportCrosstabs}
            isLoading={loading}
            loadingText="Exporting..."
            isDisabled={selectedDimensions.length === 0}
          >
            Download Crosstabs (Excel)
          </Button>
        </>
      )}

      {/* Format Reference */}
      <Box
        p={4}
        bg="gray.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="xs" mb={3}>Format Guide</Heading>
        <VStack align="start" spacing={2} fontSize="xs" color="gray.700">
          <Text>
            <strong>CSV:</strong> Use for Excel, Google Sheets, R, Python pandas
          </Text>
          <Text>
            <strong>Excel:</strong> Formatted workbook with metadata sheet
          </Text>
          <Text>
            <strong>SPSS:</strong> For IBM SPSS Statistics (includes value labels)
          </Text>
          <Text>
            <strong>Stata:</strong> For Stata 14+ (includes variable labels)
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}

export default ExportBuilder;
